import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapCreateCustomizationDemandPayload,
  mapCreateDemandResult,
  mapCreateCustomizationProjectWithTraceResult,
  mapCustomizationCurrentUserTraceRole,
  mapCustomizationDemand,
  mapCustomizationOptions,
  mapCustomizationOpportunityContext,
  mapCustomizationOpportunityBinding,
  mapCustomizationOpportunityBindingDecision,
  mapCustomizationProjectEditOptions,
  mapCustomizationProjectEditOptionsParams,
  mapCustomizationProjectPointNameOptions,
  mapCustomizationProjectPointNameOptionsParams,
  mapCustomizationProjectListPage,
  mapCustomizationProjectListParams,
  mapCustomizationProjectDemandVersionList,
  mapCustomizationDemandVersionWithoutPlanPage,
  mapCustomizationDemandVersionWithoutPlanParams,
  mapCustomizationProjectItineraryList,
  mapCustomizationProjectSnapshot,
  mapCustomizationProjectUpdatePayload,
  mapCustomizationItineraryGenerationValidation,
  mapCustomizationDispatcherNotification,
  mapCustomizationDispatcherNotificationPayload,
  mapDispatcherSelectionCandidates,
  mapDispatcherSelectionOrderDetails,
  mapMyDispatcherWorkload,
  mapCustomizationReferenceDemandList,
  mapCustomizationRouteLinkRecognitionResult,
  mapLegacyCustomizeRouteImportPayload,
  mapCustomizationTraceCreatePayload,
  mapCustomizationTraceItem,
  mapCustomizationWorkbench,
  type CreateDemandResultRaw,
  type CreateCustomizationProjectWithTraceRaw,
  type CustomizationProjectListPageRaw,
  type CustomizationProjectDemandVersionListRaw,
  type CustomizationDemandVersionWithoutPlanPageRaw,
  type CustomizationProjectItineraryListRaw,
  type CustomizationOptionsRaw,
  type CustomizationOpportunityContextRaw,
  type CustomizationOpportunityBindingRaw,
  type CustomizationProjectEditOptionsRaw,
  type CustomizationProjectPointNameOptionsRaw,
  type CustomizationItineraryGenerationValidationRaw,
  type CustomizationDispatcherNotificationRaw,
  type CustomizationCurrentUserTraceRoleRaw,
  type CustomizationReferenceDemandListRaw,
  type CustomizationRouteLinkRecognitionResultRaw,
  type CustomizationTraceItemRaw,
  type CustomizationWorkbenchRaw,
} from "../api/adapters";
import type {
  CreateCustomizationDemandPayload,
  CreateCustomizationDemandResult,
  CreateCustomizationProjectWithTraceResult,
  CustomizationDemandDraft,
  CustomizationProjectListPage,
  CustomizationProjectListParams,
  CustomizationProjectDemandVersionList,
  CustomizationDemandVersionWithoutPlanPage,
  CustomizationDemandVersionWithoutPlanParams,
  CustomizationProjectItineraryList,
  CustomizationCurrentUserTraceRole,
  CustomizationOptions,
  CustomizationOpportunityContext,
  CustomizationOpportunityBinding,
  CustomizationOpportunityBindingDecision,
  CustomizationOptionsType,
  CustomizationProjectEditOptions,
  CustomizationProjectEditOptionsParams,
  CustomizationProjectPointNameOptions,
  CustomizationProjectPointNameOptionsParams,
  CustomizationProjectSnapshot,
  CustomizationItineraryGenerationValidationResult,
  CustomizationDispatcherNotificationPayload,
  CustomizationDispatcherNotificationResult,
  CustomizationDispatcherOrderDetailItem,
  CustomizationDispatcherOrderDetailType,
  CustomizationDispatcherSelectionCandidate,
  CustomizationMyWorkloadDetailType,
  CustomizationMyWorkloadPayload,
  CustomizationProjectUpdatePayload,
  CustomizationReferenceDemandList,
  CustomizationRouteLinkRecognitionResult,
  CustomizationTraceCreate,
  CustomizationTraceItem,
  CustomizationWorkbench,
  LegacyCustomizeRouteImportPayload,
} from "../types";

const CUSTOMIZATION_WORKBENCH_TIMEOUT_MS = 60_000;
const CUSTOMIZATION_PROJECT_EDIT_OPTIONS_TIMEOUT_MS = 60_000;
const CUSTOMIZATION_DEMAND_GENERATION_TIMEOUT_MS = 0;

/** 按专用权限读取需求版本的计调额外要求。 */
export async function fetchQuickItineraryDetails(projectId: string, demandId: string) {
  const response = await httpClient.get<ApiEnvelope<import("../../../api/generated").components["schemas"]["CustomizationQuickItineraryDetails"]>>(
    `/customization/projects/${projectId}/demands/${demandId}/quick-itinerary-details`,
  );
  const data = response.data.data;
  if (response.data.code !== "OK" || !data || data.demand_id !== demandId) {
    throw new Error("额外要求接口返回了无效版本");
  }
  return { demandId: data.demand_id, details: data.quick_itinerary_details ?? "" };
}

/** 保存指定版本的计调原文，普通需求保存不携带该字段。 */
export async function saveQuickItineraryDetails(projectId: string, demandId: string, details: string) {
  const response = await httpClient.patch<ApiEnvelope<import("../../../api/generated").components["schemas"]["CustomizationQuickItineraryDetails"]>>(
    `/customization/projects/${projectId}/demands/${demandId}/quick-itinerary-details`,
    { quick_itinerary_details: details },
  );
  const data = response.data.data;
  if (response.data.code !== "OK" || !data || data.demand_id !== demandId) {
    throw new Error("额外要求保存接口返回了无效版本");
  }
  return { demandId: data.demand_id, details: data.quick_itinerary_details ?? "" };
}

/** 查询定制项目列表。 */
export async function fetchCustomizationProjectList(
  params: CustomizationProjectListParams,
): Promise<CustomizationProjectListPage> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectListPageRaw>>(
    "/customization/projects",
    {
      params: mapCustomizationProjectListParams(params),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目列表接口返回了无效数据");
  }
  return mapCustomizationProjectListPage(response.data.data);
}

/** 查询商机增定制可复制的全部历史定制。 */
export async function fetchCustomizationProjectHistory(
  opportunityId: number,
  params: Pick<CustomizationProjectListParams, "page" | "pageSize"> & { keyword: string },
): Promise<CustomizationProjectListPage> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectListPageRaw>>(
    "/customization/projects/history-search",
    {
      params: {
        opportunity_id: opportunityId,
        page: params.page,
        page_size: params.pageSize,
        keyword: params.keyword,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("历史定制搜索接口返回了无效数据");
  }
  return mapCustomizationProjectListPage(response.data.data);
}

/** 查询指定商机的关联定制项目列表。 */
export async function fetchCustomizationProjectListForOpportunity(
  opportunityId: number,
  params: Pick<CustomizationProjectListParams, "page" | "pageSize" | "keyword">,
): Promise<CustomizationProjectListPage> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectListPageRaw>>(
    "/customization/projects/for-opportunity",
    {
      params: {
        opportunity_id: opportunityId,
        page: params.page,
        page_size: params.pageSize,
        keyword: params.keyword,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机关联定制接口返回了无效数据");
  }
  return mapCustomizationProjectListPage(response.data.data);
}

/** 查询从商机新增定制时的预填上下文。 */
export async function fetchCustomizationOpportunityContext(
  opportunityId: number,
): Promise<CustomizationOpportunityContext> {
  const response = await httpClient.get<ApiEnvelope<CustomizationOpportunityContextRaw>>(
    "/customization/projects/opportunity-context",
    { params: { opportunity_id: opportunityId } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机定制上下文接口返回了无效数据");
  }
  return mapCustomizationOpportunityContext(response.data.data);
}

/** 查询项目全部需求版本。 */
export async function fetchCustomizationProjectDemandVersions(
  projectId: string,
): Promise<CustomizationProjectDemandVersionList> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectDemandVersionListRaw>>(
    `/customization/projects/${projectId}/demand-versions`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("项目需求版本接口返回了无效数据");
  }
  return mapCustomizationProjectDemandVersionList(response.data.data);
}

/** 查询商机抽屉使用的项目需求版本业务概览。 */
export async function fetchCustomizationProjectDemandVersionOverviews(
  projectId: string,
): Promise<CustomizationProjectDemandVersionList> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectDemandVersionListRaw>>(
    `/customization/projects/${projectId}/demand-version-overviews`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("项目需求版本概览接口返回了无效数据");
  }
  return mapCustomizationProjectDemandVersionList(response.data.data);
}

/** 分页查询未做方案的需求版本。 */
export async function fetchCustomizationDemandVersionsWithoutPlan(
  params: CustomizationDemandVersionWithoutPlanParams,
): Promise<CustomizationDemandVersionWithoutPlanPage> {
  const response = await httpClient.get<ApiEnvelope<CustomizationDemandVersionWithoutPlanPageRaw>>(
    "/customization/projects/demand-versions/without-plan",
    { params: mapCustomizationDemandVersionWithoutPlanParams(params) },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("未做方案版本接口返回了无效数据");
  }
  return mapCustomizationDemandVersionWithoutPlanPage(response.data.data);
}

/** 删除或归档定制项目。 */
export async function deleteCustomizationProject(projectId: string): Promise<{
  mode: string;
  dependentTables: string[];
  legacySyncStatus: "pending" | "success" | "partial_failed";
}> {
  const response = await httpClient.delete<
    ApiEnvelope<{
      mode: string;
      dependent_tables?: string[];
      legacy_sync_status?: "pending" | "success" | "partial_failed";
    }>
  >(`/customization/projects/${projectId}`);
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("删除定制项目失败");
  }
  return {
    mode: response.data.data.mode,
    dependentTables: response.data.data.dependent_tables ?? [],
    legacySyncStatus: response.data.data.legacy_sync_status ?? "success",
  };
}

/** 删除或归档单个需求版本。 */
export async function deleteCustomizationDemand({
  projectId,
  demandId,
}: {
  projectId: string;
  demandId: string;
}): Promise<{ mode: string; dependentTables: string[] }> {
  const response = await httpClient.delete<
    ApiEnvelope<{ mode: string; dependent_tables?: string[] }>
  >(`/customization/projects/${projectId}/demands/${demandId}`);
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("删除需求版本失败");
  }
  return {
    mode: response.data.data.mode,
    dependentTables: response.data.data.dependent_tables ?? [],
  };
}

/** 查询项目全部行程。 */
export async function fetchCustomizationProjectItineraries(
  projectId: string,
  page: number,
): Promise<CustomizationProjectItineraryList> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectItineraryListRaw>>(
    `/customization/projects/${projectId}/itineraries`,
    { params: { page } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("项目行程接口返回了无效数据");
  }
  return mapCustomizationProjectItineraryList(response.data.data);
}

/** 查询需求定制枚举元数据。 */
export async function fetchCustomizationOptions(
  optionTypes: CustomizationOptionsType[] = ["project_fields", "demand_fields"],
): Promise<CustomizationOptions> {
  const response = await httpClient.get<ApiEnvelope<CustomizationOptionsRaw>>(
    "/customization/projects/options",
    {
      params: { type: optionTypes.join(",") },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("需求定制枚举接口返回了无效数据");
  }
  return mapCustomizationOptions(response.data.data);
}

/** 查询当前登录人在需求定制中的默认追溯角色。 */
export async function fetchCustomizationCurrentUserTraceRole(): Promise<CustomizationCurrentUserTraceRole> {
  const response = await httpClient.get<ApiEnvelope<CustomizationCurrentUserTraceRoleRaw>>(
    "/customization/projects/current-user-trace-role",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("当前登录人需求定制追溯角色接口返回了无效数据");
  }
  return mapCustomizationCurrentUserTraceRole(response.data.data);
}

/** 查询项目基础信息编辑候选。 */
export async function fetchCustomizationProjectEditOptions(
  projectId: string,
  params: CustomizationProjectEditOptionsParams = {},
): Promise<CustomizationProjectEditOptions> {
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectEditOptionsRaw>>(
    `/customization/projects/${projectId}/edit-options`,
    {
      params: mapCustomizationProjectEditOptionsParams(params),
      timeout: CUSTOMIZATION_PROJECT_EDIT_OPTIONS_TIMEOUT_MS,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目编辑候选接口返回了无效数据");
  }
  return mapCustomizationProjectEditOptions(response.data.data);
}

/** 查询项目考察点候选。 */
export async function fetchCustomizationProjectPointNames(
  projectId: string,
  params: CustomizationProjectPointNameOptionsParams = {},
): Promise<CustomizationProjectPointNameOptions> {
  const queryParams = mapCustomizationProjectPointNameOptionsParams(params);
  const response = await httpClient.get<ApiEnvelope<CustomizationProjectPointNameOptionsRaw>>(
    `/customization/projects/${projectId}/point-names`,
    {
      params: queryParams,
      timeout: CUSTOMIZATION_PROJECT_EDIT_OPTIONS_TIMEOUT_MS,
      ...(Array.isArray(queryParams.stay_area_names) && queryParams.stay_area_names.length
        ? { paramsSerializer: { indexes: null } }
        : {}),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目考察点候选接口返回了无效数据");
  }
  return mapCustomizationProjectPointNameOptions(response.data.data);
}

/** 创建空白定制草稿项目。 */
export async function createCustomizationProject(): Promise<CustomizationProjectSnapshot> {
  const response = await httpClient.post<ApiEnvelope<CustomizationWorkbenchRaw["project"]>>(
    "/customization/projects",
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目创建接口返回了无效数据");
  }
  return mapCustomizationProjectSnapshot(response.data.data as Record<string, unknown>);
}

/** 提交首条追溯并创建正式定制项目。 */
export async function createCustomizationProjectWithTrace(
  payload: CustomizationTraceCreate,
): Promise<CreateCustomizationProjectWithTraceResult> {
  const response = await httpClient.post<ApiEnvelope<CreateCustomizationProjectWithTraceRaw>>(
    "/customization/projects/draft-traces",
    mapCustomizationTraceCreatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("首条追溯建项接口返回了无效数据");
  }
  return mapCreateCustomizationProjectWithTraceResult(response.data.data);
}

/** 从旧系统外部导入入口创建全新定制项目。 */
export async function importLegacyCustomizationFromRoute(
  payload: LegacyCustomizeRouteImportPayload,
): Promise<CreateCustomizationDemandResult> {
  const response = await httpClient.post<ApiEnvelope<CreateDemandResultRaw>>(
    "/customization/projects/legacy-customize-route-import",
    mapLegacyCustomizeRouteImportPayload(payload),
    {
      timeout: CUSTOMIZATION_DEMAND_GENERATION_TIMEOUT_MS,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("旧定制导入接口返回了无效数据");
  }
  return mapCreateDemandResult(response.data.data);
}

/** 查询单项目需求定制工作台。 */
export async function fetchCustomizationWorkbench(
  projectId: string,
  options: { opportunityId?: number; view?: "workbench" | "preview" } = {},
): Promise<CustomizationWorkbench> {
  const url = options.opportunityId
    ? `/crm/opportunity-workbench/opportunities/${options.opportunityId}/customization-projects/${encodeURIComponent(projectId)}/workbench`
    : `/customization/projects/${encodeURIComponent(projectId)}/workbench`;
  const response = await httpClient.get<ApiEnvelope<CustomizationWorkbenchRaw>>(
    url,
    {
      timeout: CUSTOMIZATION_WORKBENCH_TIMEOUT_MS,
      ...(options.view && !options.opportunityId ? { params: { view: options.view } } : {}),
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("需求定制工作台接口返回了无效数据");
  }
  return mapCustomizationWorkbench(response.data.data);
}

/** 提交商机归属决策并返回后端计算状态。 */
export async function resolveCustomizationOpportunityBinding(
  projectId: string,
  payload: CustomizationOpportunityBindingDecision,
): Promise<CustomizationOpportunityBinding> {
  const response = await httpClient.post<ApiEnvelope<CustomizationOpportunityBindingRaw>>(
    `/customization/projects/${projectId}/opportunity-binding/resolve`,
    mapCustomizationOpportunityBindingDecision(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("商机归属接口返回了无效数据");
  }
  return mapCustomizationOpportunityBinding(response.data.data);
}

/** 保存项目级必填字段。 */
export async function updateCustomizationProject(
  projectId: string,
  payload: CustomizationProjectUpdatePayload,
): Promise<CustomizationProjectSnapshot> {
  const response = await httpClient.patch<ApiEnvelope<CustomizationWorkbenchRaw["project"]>>(
    `/customization/projects/${projectId}`,
    mapCustomizationProjectUpdatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目字段保存接口返回了无效数据");
  }
  return mapCustomizationProjectSnapshot(response.data.data as Record<string, unknown>);
}

/** 刷新并保存当前商机名称。*/
export async function refreshCustomizationProjectName(
  projectId: string,
): Promise<CustomizationProjectSnapshot> {
  const response = await httpClient.patch<ApiEnvelope<CustomizationWorkbenchRaw["project"]>>(
    `/customization/projects/${projectId}/name`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制项目名称刷新接口返回了无效数据");
  }
  return mapCustomizationProjectSnapshot(response.data.data as Record<string, unknown>);
}

/** 查询项目内指定定制需求版本。 */
export async function fetchCustomizationDemand(
  projectId: string,
  demandId: string,
  options: { view?: "workbench" | "preview" } = {},
): Promise<CustomizationDemandDraft> {
  const response = await httpClient.get<ApiEnvelope<CreateDemandResultRaw["demand"]>>(
    `/customization/projects/${projectId}/demands/${demandId}`,
    options.view ? { params: { view: options.view } } : undefined,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制需求版本接口返回了无效数据");
  }
  return mapCustomizationDemand(response.data.data);
}

/** 查询追溯 JD 编号对应的参考定制预览。 */
export async function fetchCustomizationReferenceDemands(
  projectId: string,
  keyword: string,
): Promise<CustomizationReferenceDemandList> {
  const response = await httpClient.get<ApiEnvelope<CustomizationReferenceDemandListRaw>>(
    `/customization/projects/${projectId}/reference-demands`,
    {
      params: { keyword, limit: 3 },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("参考定制预览接口返回了无效数据");
  }
  return mapCustomizationReferenceDemandList(response.data.data);
}

/** 预览识别追溯文本中的旧系统行程直链。 */
export async function previewCustomizationRouteLink(
  projectId: string,
  content: string,
): Promise<CustomizationRouteLinkRecognitionResult> {
  const response = await httpClient.post<ApiEnvelope<CustomizationRouteLinkRecognitionResultRaw>>(
    `/customization/projects/${projectId}/trace-recognition/route-link`,
    { content },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("行程直链预览接口返回了无效数据");
  }
  return mapCustomizationRouteLinkRecognitionResult(response.data.data);
}

/** 新增需求追溯记录。 */
export async function createCustomizationTrace(
  projectId: string,
  payload: CustomizationTraceCreate,
): Promise<CustomizationTraceItem> {
  const response = await httpClient.post<ApiEnvelope<CustomizationTraceItemRaw>>(
    `/customization/projects/${projectId}/traces`,
    mapCustomizationTraceCreatePayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("需求追溯接口返回了无效数据");
  }
  return mapCustomizationTraceItem(response.data.data);
}

/** 基于勾选追溯记录生成定制需求版本。 */
export async function createCustomizationDemand(
  projectId: string,
  payload: CreateCustomizationDemandPayload,
): Promise<CreateCustomizationDemandResult> {
  const response = await httpClient.post<ApiEnvelope<CreateDemandResultRaw>>(
    `/customization/projects/${projectId}/demands`,
    mapCreateCustomizationDemandPayload(payload),
    {
      timeout: CUSTOMIZATION_DEMAND_GENERATION_TIMEOUT_MS,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("定制需求生成接口返回了无效数据");
  }
  return mapCreateDemandResult(response.data.data);
}

/** 校验当前需求版本是否允许生成行程。 */
export async function validateCustomizationItineraryGeneration(
  projectId: string,
  demandId: string,
): Promise<CustomizationItineraryGenerationValidationResult> {
  const response = await httpClient.post<ApiEnvelope<CustomizationItineraryGenerationValidationRaw>>(
    `/customization/projects/${projectId}/demands/${demandId}/itinerary-generation-validation`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("生成行程校验接口返回了无效数据");
  }
  return mapCustomizationItineraryGenerationValidation(response.data.data);
}

/** 查询第一计调选择弹窗候选。 */
export async function fetchDispatcherSelectionCandidates(): Promise<CustomizationDispatcherSelectionCandidate[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    "/customization/projects/dispatcher-selection-candidates",
  );
  if (response.data.code !== "OK") {
    throw new Error("第一计调选择候选接口返回了无效数据");
  }
  return mapDispatcherSelectionCandidates(response.data.data);
}

/** 查询指定计调未接单/在跟单明细。 */
export async function fetchDispatcherSelectionOrderDetails(
  userId: string,
  detailType: CustomizationDispatcherOrderDetailType,
): Promise<CustomizationDispatcherOrderDetailItem[]> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    `/customization/projects/dispatcher-selection-candidates/${encodeURIComponent(userId)}/order-details`,
    { params: { detail_type: detailType } },
  );
  if (response.data.code !== "OK") {
    throw new Error("计调未接单/在跟单明细接口返回了无效数据");
  }
  return mapDispatcherSelectionOrderDetails(response.data.data);
}

/** 查询当前登录计调「我的未接单」四类工作量与明细。 */
export async function fetchMyDispatcherWorkload(
  detailType: CustomizationMyWorkloadDetailType = "pending",
): Promise<CustomizationMyWorkloadPayload> {
  const response = await httpClient.get<ApiEnvelope<unknown>>(
    "/customization/projects/my-dispatcher-workload",
    { params: { detail_type: detailType } },
  );
  if (response.data.code !== "OK") {
    throw new Error("我的未接单接口返回了无效数据");
  }
  return mapMyDispatcherWorkload(response.data.data);
}

/** 通知当前项目第一计调查看行程更新。 */
export async function notifyCustomizationDispatcher(
  projectId: string,
  payload: CustomizationDispatcherNotificationPayload = {},
): Promise<CustomizationDispatcherNotificationResult> {
  const response = await httpClient.post<ApiEnvelope<CustomizationDispatcherNotificationRaw>>(
    `/customization/projects/${projectId}/dispatcher-notification`,
    mapCustomizationDispatcherNotificationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("通知计调接口返回了无效数据");
  }
  return mapCustomizationDispatcherNotification(response.data.data);
}

/** 完成外部智能体生成前的前端运行时边界；行程由智能体页面生成并固化。 */
export async function startCustomizationItineraryGeneration(
  projectId: string,
  demandId: string,
  orderVersionKey: string,
): Promise<void> {
  void projectId;
  void demandId;
  void orderVersionKey;
}
